import type { MediaItem, NoticeCornerRadius, NoticeDataWidgetLayer, NoticeDataWidgetSource, NoticeFontWeight, NoticeImageLayer, NoticeLayer, NoticeTextLayer, NoticeVideoLayer } from '@cornerstone/shared';
import { STATIONC_TEMPLATE_ASSETS, assetMediaId, assetForMediaId, isVideoAsset, type StationCTemplateAsset, type StationCTemplateAssetKey } from './stationcTemplateAssets';

// =============================================================================
// Station C Figma templates 1–6 — file DP1eoxbusBnlm3ekBuMUmm (2026-09-22). Every number below is
// in the 2160×3840 portrait design space; `buildStationCFigmaTemplate()` fits that box into the
// current canvas (uniform scale, centred) the same way the 저전력 시계 template does.
//
// Static art (backgrounds, card frames, the "Merry Christmas" lettering) is bundled as images
// (see stationcTemplateAssets.ts); editable text is a text layer with the Figma font, weight,
// line height and letter spacing; replaceable images/logos/backgrounds are "slots" the gallery
// fills from the media library (defaults come from the Figma sample assets).
// =============================================================================

export const FIGMA_DESIGN = { width: 2160, height: 3840 } as const;
export const FONT_PRETENDARD = 'Pretendard';
export const FONT_SOLMOE = 'Solmoe KimDaeGeon';

type Box = readonly [x: number, y: number, width: number, height: number];
/** Figma image crop expressed the player's way: scale relative to the fit result plus centre offsets (% of the box). */
type Crop = { mediaScale: number; mediaOffsetX: number; mediaOffsetY: number };
export type FigmaTemplateSlot = {
  id: string; label: string;
  /** `background` slots accept image or video (the designer plans video backgrounds); others image only. */
  kind: 'logo' | 'image' | 'background';
  /** Bundled default; `null` = an empty slot until the user picks media. */
  defaultAsset: StationCTemplateAssetKey | null;
  box: Box; fit: 'cover' | 'contain' | 'stretch'; cornerRadius?: NoticeCornerRadius; stage: number;
  /** Applies only while the bundled default is shown; user-picked media starts from a plain fit. */
  defaultCrop?: Crop;
};
export type FigmaTemplateText = {
  id: string; label: string; text: string; box: Box;
  fontSize: number; fontWeight: NoticeFontWeight; color: string; lineHeight: number; letterSpacing?: number;
  align?: NoticeTextLayer['align']; verticalAlign?: NoticeTextLayer['verticalAlign']; fontFamily?: string;
  backgroundColor?: string; cornerRadius?: NoticeCornerRadius; opacity?: number; stage: number;
  /** Fixed decoration (divider, pill outline): drawn but not offered as an editable field. */
  fixed?: boolean;
  /** AI 초안 튜닝. 없으면 라벨을 역할로 쓴다. */
  ai?: FigmaTextAi;
};
/** AI 초안이 이 칸을 채우는 규칙 — role 은 프롬프트에 그대로 들어간다. */
export type FigmaTextAi = {
  role: string;
  /** 시안 여백 기준 최대 줄 수. 없으면 박스 높이로 계산한다. */
  lines?: number;
  /** 일시·장소·문의처처럼 입력에 없으면 비워 두는 사실 정보. */
  fact?: boolean;
  /** 의도에 맞으면 기본 문구를 그대로 둬도 되는 분류 라벨(공지사항·NEWS). */
  keep?: boolean;
  /** 입력에 날짜가 없으면 오늘 날짜를 쓰는 게시일. */
  today?: boolean;
};
type FigmaTemplateArt = { id: string; label: string; asset: StationCTemplateAssetKey; box: Box; fit: 'cover' | 'contain' | 'stretch'; stage: number };
/** Live data widget (date, weather, policy news). Source and look are fixed by the template; the data refreshes on the device. */
export type FigmaTemplateWidget = {
  id: string; label: string; box: Box; source: NoticeDataWidgetSource; title: string;
  fontSize: number; color: string; stage: number; fontWeight?: number; fontFamily?: string; align?: NoticeDataWidgetLayer['align'];
  weatherLayout?: NoticeDataWidgetLayer['weatherLayout']; weatherFields?: NoticeDataWidgetLayer['weatherFields'];
};
export type StationCFigmaTemplate = {
  /** `figmaNode` is absent for Station C designs made outside the Figma hand-off (e.g. the policy-news A-2 draft). */
  id: string; category: string; name: string; description: string; figmaNode?: string; canvasFill: string;
  /** AI 초안 — guide: 템플릿 성격(프롬프트), placeholder: '전달할 내용' 입력 예시. */
  ai?: { guide: string; placeholder: string };
  /** Draw order = array order. */
  layers: readonly (({ type: 'art' } & FigmaTemplateArt) | ({ type: 'slot' } & FigmaTemplateSlot) | ({ type: 'text' } & FigmaTemplateText) | ({ type: 'widget' } & FigmaTemplateWidget))[];
};

const art = (id: string, label: string, asset: StationCTemplateAssetKey, box: Box, fit: FigmaTemplateArt['fit'] = 'cover', stage = 0): StationCFigmaTemplate['layers'][number] =>
  ({ type: 'art', id, label, asset, box, fit, stage });
const slot = (id: string, label: string, kind: FigmaTemplateSlot['kind'], defaultAsset: StationCTemplateAssetKey | null, box: Box,
  extra: Partial<Pick<FigmaTemplateSlot, 'fit' | 'cornerRadius' | 'stage' | 'defaultCrop'>> = {}): StationCFigmaTemplate['layers'][number] =>
  ({ type: 'slot', id, label, kind, defaultAsset, box, fit: extra.fit ?? (kind === 'logo' ? 'contain' : 'cover'), stage: extra.stage ?? (kind === 'background' ? 0 : 1),
    ...(extra.cornerRadius !== undefined ? { cornerRadius: extra.cornerRadius } : {}), ...(extra.defaultCrop ? { defaultCrop: extra.defaultCrop } : {}) });
const text = (id: string, label: string, value: string, box: Box, fontSize: number, fontWeight: NoticeFontWeight, color: string, lineHeight: number,
  extra: Partial<Omit<FigmaTemplateText, 'id' | 'label' | 'text' | 'box' | 'fontSize' | 'fontWeight' | 'color' | 'lineHeight'>> = {}): StationCFigmaTemplate['layers'][number] =>
  ({ type: 'text', id, label, text: value, box, fontSize, fontWeight, color, lineHeight, stage: extra.stage ?? 2, ...extra });
const widget = (id: string, label: string, source: NoticeDataWidgetSource, box: Box, fontSize: number, color: string,
  extra: Partial<Omit<FigmaTemplateWidget, 'id' | 'label' | 'source' | 'box' | 'fontSize' | 'color'>> = {}): StationCFigmaTemplate['layers'][number] =>
  ({ type: 'widget', id, label, source, box, fontSize, color, title: extra.title ?? label, stage: extra.stage ?? 2, fontFamily: FONT_PRETENDARD, ...extra });

const black = '#000000', white = '#FFFFFF', blue = '#0080fb', grey = '#5a5a5a';
const SAMPLE_BODY = '춘천시 지역경제 촉진 등에 관한 조례 제 5조의 규정에 따라 2026년도 춘천시 창업지원금(중소기업 동행지원) 지원 공고를 안내합니다.\n\n지원 대상과 신청 방법, 접수 기간을 확인하시고 기한 내에 신청해 주세요.';
const FULL: Box = [0, 0, FIGMA_DESIGN.width, FIGMA_DESIGN.height];
/** Card frame PNGs are cropped to (40, 392)–(2160, 3840): the 1680×3004 card at (240, 592) plus its shadow. */
const CARD_FRAME: Box = [40, 392, 2120, 3448];
const STATIONC_LOGO: Box = [240, 240, 290.43, 70];
const HALLYM_LOGO_W = 438.47, HALLYM_LOGO_H = 110;
// AI 초안 튜닝에서 여러 템플릿이 같이 쓰는 문구.
const EVENT_BRIEF = '예) 신입생 환영 행사. 12월 10일~12일 학생회관 대강당, 대상 2027학년도 신입생, 문의 학생지원팀';
const NOTICE_BRIEF = '예) 창업지원사업 선정기업 발표. 12개사 선정, 협약은 12월 5일까지, 문의 창업지원단';
const EVENT_TITLE: FigmaTextAi = { role: '행사 이름. 포스터의 주인공이라 짧고 굵게 1~3줄. 줄마다 뜻이 이어지게 끊고 한 낱말만 외따로 남기지 않는다.' };
const EVENT_DATE: FigmaTextAi = { role: '행사 날짜(시간이 있으면 함께). sample과 같은 형식(예: 2026. 12.10 - 12 / 2026. 10.15 (수) 13:00). 한 줄에 안 들어가면 날짜만 쓰고 시간은 안내 칸에 적는다.', fact: true };
const EVENT_PLACE: FigmaTextAi = { role: '행사 장소.', fact: true };
const EVENT_EYEBROW: FigmaTextAi = { role: '제목 위 한 줄 머리말. 행사 성격이나 대상을 짧게(예: 2027년 행사소식입니다, 신입생 여러분 환영합니다).' };
const EVENT_NOTE = '작은 안내 문단. userBrief의 대상·참여 방법 같은 부가 정보나 짧은 행사 소개.';
const NOTICE_TITLE: FigmaTextAi = { role: '카드 위 큰 머리글. 공지 분류를 짧게(예: 공지사항, 채용 공고, 학사 안내).', keep: true };
const NOTICE_HEADLINE: FigmaTextAi = { role: '공지 핵심을 요약한 제목. 한 줄에 다 들어가게 짧게.' };
const NOTICE_DATE: FigmaTextAi = { role: '게시일(공지를 올리는 날). YYYY-MM-DD 형식. 점검·행사·마감 날짜가 아니다.', today: true };
const NOTICE_BODY = '공지 본문. 완결된 문장으로, 첫 문단에 핵심을 한두 문장, 다음 문단에 일정·대상·방법 같은 세부(userBrief에 있는 것만), 마지막에 문의처(있을 때만). 문단 사이는 빈 줄.';

export const STATIONC_FIGMA_TEMPLATES: readonly StationCFigmaTemplate[] = [
  {
    id: 'figma-card-news', category: '카드뉴스', name: '1. 카드뉴스', figmaNode: '1:79', canvasFill: white,
    description: '연한 파란 배경 위 흰 카드. 카드 상단 사진과 좌상단 로고를 바꿀 수 있고, 배지·제목·본문·날짜를 편집합니다.',
    ai: { guide: '카드뉴스 한 장. 흰 카드 위쪽에 사진, 아래에 배지·제목·본문·날짜가 온다. 소식 한 건을 제목 한 줄 요약과 짧은 본문으로 전한다.',
      placeholder: '예) 2026년 춘천시 창업지원금 지원 공고. 대상 춘천 소재 중소기업, 접수 11월 1일~30일, 문의 창업지원단' },
    layers: [
      art('bg', '배경', 'soft-blue-bg', FULL),
      art('card', '카드', 'card-frame-news', CARD_FRAME, 'stretch'),
      slot('logo', '로고 (좌상단)', 'logo', 'logo-stationc', STATIONC_LOGO),
      text('title', '상단 제목', '신규 소식 & 뉴스', [240, 344, 1680, 120], 120, 700, black, 1, { letterSpacing: -2.4, verticalAlign: 'middle', stage: 1,
        ai: { role: '카드 위 큰 머리글. 이 소식이 속한 분류를 짧게(예: 신규 소식 & 뉴스, 창업 지원 소식, 학사 안내).', keep: true } }),
      // Figma crops the sample 137% wide, shifted left 21% and showing the top of the photo.
      slot('image', '카드 상단 이미지', 'image', 'sample-photo', [240, 592, 1680, 1000], { cornerRadius: [140, 140, 0, 0], stage: 1, defaultCrop: { mediaScale: 1.374, mediaOffsetX: -2.3, mediaOffsetY: 25.4 } }),
      text('image-dim', '이미지 어둡게', '', [240, 592, 1680, 1000], 24, 400, black, 1.2, { backgroundColor: 'rgba(0,0,0,0.15)', cornerRadius: [140, 140, 0, 0], fixed: true, stage: 1 }),
      text('badge-outline', '배지 테두리', '', [380, 1722, 236, 95], 24, 400, blue, 1.2, { backgroundColor: blue, cornerRadius: 100, fixed: true }),
      text('badge', '배지', 'NEWS', [384, 1726, 228, 87], 56, 600, blue, 1.26, { backgroundColor: white, cornerRadius: 96, align: 'center', verticalAlign: 'middle',
        ai: { role: '제목 위 작은 알약 배지. 소식 종류를 한 단어로(예: NEWS, 공고, 모집, 행사, 안내).', keep: true } }),
      text('headline', '카드 제목', '2026년도 춘천시 창업지원\n자금(중소기업 동행) 지원 공고', [380, 1887, 1400, 252], 100, 700, black, 1.26, { verticalAlign: 'top', stage: 3,
        ai: { role: '소식 핵심을 요약한 제목. 줄을 나눌 때는 의미 단위로 끊는다.' } }),
      text('body', '본문', SAMPLE_BODY, [380, 2263, 1400, 1100], 60, 400, grey, 1.4, { letterSpacing: 1.2, verticalAlign: 'top', stage: 3,
        ai: { role: '소식 내용. userBrief에 있는 사실(무엇을·누구에게·언제까지·어떻게)만 1~3문단으로 정리한다. 문단 사이는 빈 줄.', lines: 12 } }),
      text('date', '날짜', '2026-11-25', [380, 3396, 1400, 70], 50, 400, 'rgba(0,0,0,0.35)', 1.4, { letterSpacing: 1, verticalAlign: 'top', stage: 4,
        ai: { role: '게시일(소식을 올리는 날). YYYY-MM-DD 형식. 행사·마감 날짜가 아니다.', today: true } }),
    ],
  },
  {
    // 정책브리핑 A-2 시안(2026-09-27): 카드뉴스 배경 위 흰 카드(사진 칸 없는 공지 프레임)에 날짜·날씨 알약과 부처별 정책뉴스 6건.
    id: 'figma-policy-news', category: '카드뉴스', name: '1-1. 오늘의 정책 소식 (실시간)', canvasFill: white,
    description: '카드뉴스 배경에 대한민국 정책브리핑 정책뉴스를 부처별 최신 6건으로 자동 갱신합니다. 상단에 오늘 날짜·요일과 설치 지역 날씨를 함께 보여줍니다.',
    layers: [
      art('bg', '배경', 'soft-blue-bg', FULL),
      art('card', '카드', 'card-frame-notice', CARD_FRAME, 'stretch'),
      slot('logo', '로고 (좌상단)', 'logo', 'logo-stationc', STATIONC_LOGO),
      text('title', '상단 제목', '오늘의 정책 소식', [240, 344, 1680, 120], 120, 700, black, 1, { letterSpacing: -2.4, verticalAlign: 'middle', stage: 1 }),
      // Date is right-aligned against the divider so a longer date ("12월 31일 (수)") grows leftwards inside the pill.
      text('info-pill', '날짜·날씨 배경', '', [1230, 212, 690, 128], 24, 400, black, 1.2, { backgroundColor: white, cornerRadius: 64, fixed: true, stage: 1 }),
      widget('date', '오늘 날짜·요일', { kind: 'date', format: 'month-day-weekday' }, [1250, 212, 350, 128], 56, '#1A1A1A', { fontWeight: 600, align: 'right', stage: 1 }),
      text('info-divider', '날짜·날씨 구분선', '', [1618, 250, 4, 52], 24, 400, black, 1.2, { backgroundColor: '#E0E0E0', fixed: true, stage: 1 }),
      widget('weather', '설치 지역 날씨', { kind: 'weather', locationMode: 'device', latitude: 0, longitude: 0, placeName: '설치 지역' }, [1640, 212, 250, 128], 21, '#1A1A1A',
        { align: 'left', weatherLayout: 'horizontal', weatherFields: { place: false, icon: true, temperature: true, humidity: false, wind: false }, stage: 1 }),
      text('badge-outline', '배지 테두리', '', [352, 708, 236, 95], 24, 400, blue, 1.2, { backgroundColor: blue, cornerRadius: 100, fixed: true }),
      text('badge', '배지', 'NEWS', [356, 712, 228, 87], 56, 600, blue, 1.26, { backgroundColor: white, cornerRadius: 96, align: 'center', verticalAlign: 'middle' }),
      text('card-title', '카드 제목', '정책브리핑 정책뉴스', [624, 708, 880, 95], 64, 700, black, 1.2, { letterSpacing: -1, verticalAlign: 'middle' }),
      text('order', '정렬 안내', '부처별 최신', [1500, 708, 308, 95], 48, 400, 'rgba(0,0,0,0.35)', 1.2, { align: 'right', verticalAlign: 'middle' }),
      widget('news', '정책브리핑 정책뉴스 목록', { kind: 'policyNews' }, [352, 850, 1456, 2654], 80, '#111111', { title: '정책브리핑 정책뉴스', stage: 3 }),
    ],
  },
  {
    id: 'figma-poster-1', category: '행사 안내', name: '2. 행사 포스터 ①', figmaNode: '1:1039', canvasFill: white,
    description: '파스텔 오브제가 움직이는 배경 영상과 좌상단 로고. 안내 문구·제목·일시·장소·하단 안내를 편집합니다.',
    ai: { guide: '행사 안내 포스터. 흰 배경에 파스텔 오브제 영상, 검은 글자. 위에서부터 안내 문구, 아주 큰 행사 제목, 일시·장소, 맨 아래 작은 부가 안내.', placeholder: EVENT_BRIEF },
    layers: [
      slot('background', '배경 (이미지·영상)', 'background', 'poster1-bg', FULL),
      slot('logo', '로고 (좌상단)', 'logo', 'logo-hallym', [240, 240, HALLYM_LOGO_W, HALLYM_LOGO_H]),
      text('eyebrow', '안내 문구', '2027년 행사소식입니다', [240, 680, 1680, 126], 100, 500, black, 1.26, { ai: EVENT_EYEBROW }),
      text('title', '제목', '한림대학교\n신입생 환영 행사\n소식', [240, 836, 1680, 720], 200, 800, black, 1.2, { verticalAlign: 'top', stage: 3, ai: EVENT_TITLE }),
      text('date', '일시', '2026. 12.10 - 12', [240, 1726, 1680, 120], 120, 600, '#1d3c75', 1, { letterSpacing: 2.4, stage: 4, ai: EVENT_DATE }),
      text('place', '장소', '한림대학교 학생회관 대강당', [240, 1858, 1680, 98], 70, 600, '#1d3c75', 1.4, { stage: 4, ai: EVENT_PLACE }),
      text('footnote', '하단 안내', '춘천시 지역경제 촉진 등에 관한 조례 제 5조의 규정에 따라\n2026년도 춘천시 창업지원금(중소기업 동행지원) 안내\n문의: 학생지원팀', [240, 3270, 1680, 330], 44, 400, 'rgba(0,0,0,0.8)', 1.5, { letterSpacing: -0.88, verticalAlign: 'top', stage: 4,
        ai: { role: '맨 아래 작은 부가 안내. userBrief에 있는 대상·신청 방법·문의처를 한 줄에 하나씩(없는 항목은 쓰지 않는다).', fact: true, lines: 3 } }),
    ],
  },
  {
    id: 'figma-poster-2', category: '행사 안내', name: '2. 행사 포스터 ②', figmaNode: '1:432', canvasFill: blue,
    description: '파란 타일이 움직이는 배경 영상과 우상단 로고. 상단 안내·제목·부제·일시·장소를 편집합니다.',
    ai: { guide: '행사 안내 포스터. 파란 타일 영상 배경에 흰 글자. 좌상단에 작은 안내 문단, 아래쪽에 큰 제목과 부제, 맨 아래 연도·일시·장소.', placeholder: EVENT_BRIEF },
    layers: [
      slot('background', '배경 (이미지·영상)', 'background', 'poster2-bg', FULL),
      text('note', '상단 안내', '2026년도 춘천시 창업지원금(중소기업 동행지원)\n지역경제 촉진 등에 관한 조례 제 5조의 규정\n창업지원금(중소기업 동행지원)', [180, 180, 1300, 558], 44, 400, white, 1.5, { letterSpacing: -1.32, opacity: 0.8, verticalAlign: 'top', stage: 1,
        ai: { role: `좌상단 ${EVENT_NOTE} 1~3줄.`, lines: 3 } }),
      slot('logo', '로고 (우상단)', 'logo', 'logo-hallym-white', [1537.15, 180, HALLYM_LOGO_W, HALLYM_LOGO_H]),
      text('title', '제목', '한림대학교\n신입생 환영 행사\n소식', [180, 2313.4, 1680, 750], 200, 800, white, 1.25, { verticalAlign: 'top', ai: EVENT_TITLE }),
      text('subtitle', '부제', '2027년 행사소식입니다', [180, 3093.4, 1680, 139], 110, 200, white, 1.26, { stage: 3,
        ai: { role: '제목 아래 한 줄 부제. 행사 성격이나 짧은 한마디(예: 2027년 행사소식입니다).' } }),
      // Figma box is 215 wide with overflow hidden; the canvas wraps instead, so give the year a little slack.
      text('year', '연도', '2026.', [180, 3462.4, 240, 100], 80, 400, white, 1, { letterSpacing: 1.6, verticalAlign: 'bottom', stage: 4,
        ai: { role: '행사 연도만(형식: 2026.). 날짜에 연도가 없으면 referenceDate 기준으로 정한다.', fact: true } }),
      text('date', '일시', '12.10 - 12', [430, 3462.4, 1430, 100], 100, 500, white, 1, { letterSpacing: 2, verticalAlign: 'bottom', stage: 4,
        ai: { role: '연도를 뺀 행사 날짜·시간. sample과 같은 형식(예: 12.10 - 12 / 10.15 (수) 13:00 - 17:00).', fact: true } }),
      text('place', '장소', '한림대학교 학생회관 대강당', [180, 3562.4, 1680, 98], 70, 500, 'rgba(255,255,255,0.9)', 1.4, { stage: 4, ai: EVENT_PLACE }),
    ],
  },
  {
    id: 'figma-poster-3', category: '행사 안내', name: '2. 행사 포스터 ③ (사진)', figmaNode: '1:478', canvasFill: blue,
    description: '전체 사진 배경(교체 가능) 위에 어두운 그라데이션. 좌상단 로고와 우측 정렬 문구·제목·일시·장소를 편집합니다.',
    ai: { guide: '사진 배경 행사 포스터. 어두운 그라데이션 위 흰 글자, 글자는 모두 오른쪽 정렬. 우상단 작은 안내, 아래쪽에 안내 문구, 큰 제목, 일시·장소.', placeholder: EVENT_BRIEF },
    layers: [
      // Figma: 5161×3344 photo at (-1442, -3) — narrower than cover; the navy gradient hides the gap below 3341.
      slot('background', '배경 사진 (이미지·영상)', 'background', 'sample-photo', FULL, { defaultCrop: { mediaScale: 0.877, mediaOffsetX: 2.7, mediaOffsetY: -6.5 } }),
      art('overlay', '어둡게 오버레이', 'poster3-overlay', FULL, 'stretch'),
      slot('logo', '로고 (좌상단)', 'logo', 'logo-hallym-dark', [200, 200, HALLYM_LOGO_W, HALLYM_LOGO_H]),
      text('note', '상단 안내', '2026년도 춘천시 창업지원금(중소기업 동행지원)\n춘천시 지역경제 촉진 등에 관한 조례 제 5조의 규정', [860, 200, 1100, 132], 44, 400, white, 1.5, { letterSpacing: -1.32, opacity: 0.8, align: 'right', verticalAlign: 'top', stage: 1,
        ai: { role: `우상단 ${EVENT_NOTE} 1~2줄.` } }),
      text('eyebrow', '안내 문구', '2027년 행사소식입니다', [200, 2279, 1760, 126], 100, 400, '#91c1ff', 1.26, { align: 'right', ai: EVENT_EYEBROW }),
      text('title', '제목', '한림대학교\n신입생 환영 행사', [200, 2435, 1760, 546], 210, 800, white, 1.3, { align: 'right', verticalAlign: 'top', stage: 3,
        ai: { role: '행사 이름. 1~2줄로 짧게. 줄마다 뜻이 이어지게 끊고 한 낱말만 외따로 남기지 않는다.' } }),
      text('date', '일시', '2026. 12.10 - 12', [200, 3351, 1760, 135], 135, 400, white, 1, { letterSpacing: 2.7, align: 'right', stage: 4, ai: EVENT_DATE }),
      text('place', '장소', '한림대학교 학생회관 대강당', [200, 3498, 1760, 112], 80, 500, 'rgba(255,255,255,0.9)', 1.4, { align: 'right', stage: 4, ai: EVENT_PLACE }),
    ],
  },
  {
    id: 'figma-notice-photo', category: '공지사항', name: '3. 공지사항 (사진)', figmaNode: '1:131', canvasFill: white,
    description: '흰 카드 안에 제목·날짜·사진·본문. 좌상단 로고와 카드 사진을 바꿀 수 있습니다.',
    ai: { guide: '공지사항 카드. 흰 카드 안에 공지 제목과 날짜, 구분선 아래 사진과 본문.', placeholder: NOTICE_BRIEF },
    layers: [
      art('bg', '배경', 'soft-blue-bg', FULL),
      art('card', '카드', 'card-frame-notice', CARD_FRAME, 'stretch'),
      slot('logo', '로고 (좌상단)', 'logo', 'logo-stationc', STATIONC_LOGO),
      text('title', '상단 제목', '공지사항', [240, 344, 1680, 120], 120, 700, black, 1, { letterSpacing: -2.4, verticalAlign: 'middle', stage: 1, ai: NOTICE_TITLE }),
      text('headline', '공지 제목', '창업지원사업 선정기업 발표', [380, 712, 1400, 140], 100, 700, black, 1.4, { verticalAlign: 'top', ai: NOTICE_HEADLINE }),
      text('date', '날짜', '2026-11-25', [380, 868, 1400, 76], 54, 500, 'rgba(0,0,0,0.4)', 1.4, { letterSpacing: 1.08, verticalAlign: 'top', ai: NOTICE_DATE }),
      text('divider', '구분선', '', [380, 1020, 1400, 4], 24, 400, black, 1.2, { backgroundColor: 'rgba(0,0,0,0.1)', fixed: true }),
      slot('image', '카드 사진', 'image', 'sample-photo', [380, 1148, 1400, 904], { stage: 3 }),
      text('body', '본문', '안녕하세요. 창업지원사업 선정기업 결과입니다.\n\n' + SAMPLE_BODY, [380, 2152, 1400, 1314], 64, 400, grey, 1.4, { letterSpacing: 1.28, verticalAlign: 'top', stage: 3,
        ai: { role: NOTICE_BODY, lines: 13 } }),
    ],
  },
  {
    id: 'figma-notice', category: '공지사항', name: '3. 공지사항', figmaNode: '1:1578', canvasFill: white,
    description: '흰 카드 안에 제목·날짜·본문만. 좌상단 로고를 바꿀 수 있습니다.',
    ai: { guide: '공지사항 카드. 흰 카드 안에 공지 제목과 날짜, 구분선 아래 긴 본문.', placeholder: NOTICE_BRIEF },
    layers: [
      art('bg', '배경', 'soft-blue-bg', FULL),
      art('card', '카드', 'card-frame-notice', CARD_FRAME, 'stretch'),
      slot('logo', '로고 (좌상단)', 'logo', 'logo-stationc', STATIONC_LOGO),
      text('title', '상단 제목', '공지사항', [240, 344, 1680, 120], 120, 700, black, 1, { letterSpacing: -2.4, verticalAlign: 'middle', stage: 1, ai: NOTICE_TITLE }),
      text('headline', '공지 제목', '창업지원사업 선정기업 발표', [380, 712, 1400, 140], 100, 700, black, 1.4, { verticalAlign: 'top', ai: NOTICE_HEADLINE }),
      text('date', '날짜', '2026-11-25', [380, 868, 1400, 76], 54, 500, 'rgba(0,0,0,0.4)', 1.4, { letterSpacing: 1.08, verticalAlign: 'top', ai: NOTICE_DATE }),
      text('divider', '구분선', '', [380, 1020, 1400, 4], 24, 400, black, 1.2, { backgroundColor: 'rgba(0,0,0,0.1)', fixed: true }),
      text('body', '본문', '안녕하세요. 창업지원사업 선정기업 결과입니다.\n\n' + SAMPLE_BODY, [380, 1148, 1400, 2318], 64, 400, grey, 1.4, { letterSpacing: 1.28, verticalAlign: 'top', stage: 3,
        ai: { role: NOTICE_BODY, lines: 24 } }),
    ],
  },
  {
    id: 'figma-greeting', category: '그리팅', name: '4. 그리팅 메시지', figmaNode: '1:535', canvasFill: white,
    description: '흰 배경에 파란 구체가 움직이는 배경 영상. 환영 제목과 부제를 편집합니다.',
    ai: { guide: '환영 인사 화면. 흰 배경에 파란 구체 영상, 가운데 위쪽에 큰 환영 제목과 파란 부제 한 줄.', placeholder: '예) 10월 2일 춘천시장님 방문 환영' },
    layers: [
      slot('background', '배경 (이미지·영상)', 'background', 'greeting-bg', FULL),
      text('title', '제목', '센터장님 방문을\n환영합니다.', [240, 1000, 1680, 530], 210, 800, black, 1.26, { verticalAlign: 'top',
        ai: { role: '큰 환영 인사 1~2줄. 방문자 이름·직함·단체명은 userBrief에 있을 때만 쓰고 사람에게는 존칭(님)을 붙인다. 없으면 방문객 모두를 환영한다.' } }),
      text('subtitle', '부제', '한림대학교 방문을 환영합니다.', [240, 1580, 1680, 126], 100, 600, '#2377cc', 1.26, { stage: 3,
        ai: { role: '제목 아래 한 줄 인사. 방문을 반기거나 감사하는 짧은 문장으로 제목과 다른 말을 쓴다. organization 이름을 넣어도 된다.' } }),
    ],
  },
  {
    id: 'figma-campaign', category: '캠페인', name: '5. 캠페인 (신년·설·추석)', figmaNode: '1:579', canvasFill: white,
    description: '산과 학 일러스트가 움직이는 배경 영상. 솔뫼 김대건체 인사말과 부제를 편집합니다.',
    ai: { guide: '명절·시즌 인사 캠페인. 산과 학이 움직이는 배경 영상, 아래쪽 가운데에 붓글씨체 인사말과 작은 부제.', placeholder: '예) 추석 인사. 풍요롭고 건강한 한가위 되시길' },
    layers: [
      slot('background', '배경 (이미지·영상)', 'background', 'campaign-bg', FULL),
      text('title', '인사말', '풍요로운\n한가위 되세요', [220, 2950.56, 1720, 427], 194, 500, black, 1.1, { letterSpacing: -3.88, align: 'center', verticalAlign: 'top', fontFamily: FONT_SOLMOE,
        ai: { role: '큰 명절·시즌 인사말 1~2줄, 완결된 인사 문장으로(예: 새해 복 많이 받으세요, 풍요로운 한가위 되세요). 신년·설·추석 등 userBrief의 때에 맞춘다.' } }),
      text('subtitle', '부제', '풍성한 한가위 보내시고,\n늘 건강과 행복이 함께하시길 기원합니다.', [220, 3417.4, 1720, 182], 70, 300, '#b8a180', 1.3, { letterSpacing: -1.4, align: 'center', verticalAlign: 'top', fontFamily: FONT_SOLMOE, stage: 3,
        ai: { role: '인사말 아래 작은 기원 문장 1~2줄.' } }),
    ],
  },
  {
    id: 'figma-christmas', category: '캠페인', name: '6. 캠페인 (크리스마스)', figmaNode: '1:817', canvasFill: '#1e9a66',
    description: '오너먼트 일러스트, Merry Christmas 레터링, 하단 인사말이 모두 배경 영상에 들어 있습니다. 편집할 문구는 없고 배경 영상만 바꿀 수 있습니다.',
    layers: [
      slot('background', '배경 (이미지·영상)', 'background', 'christmas-bg', FULL),
    ],
  },
];

export type StationCFigmaLayer = NoticeTextLayer | NoticeImageLayer | NoticeVideoLayer | NoticeDataWidgetLayer;
export type StationCFigmaDraft = { canvasWidth: number; canvasHeight: number; canvasFill: string; canvasFillOpacity: number; durationSec: number; layers: StationCFigmaLayer[] };
export type StationCFigmaOptions = {
  templateId: string; canvasWidth: number; canvasHeight: number; durationSec: number;
  textValues?: Readonly<Record<string, string>>;
  /** slot id → media id from the current library. Missing/empty = bundled default (or nothing when there is none). */
  slotMedia?: Readonly<Record<string, string>>;
  mediaList?: readonly MediaItem[];
};

export const figmaTemplateSlots = (template: StationCFigmaTemplate): FigmaTemplateSlot[] =>
  template.layers.filter((layer): layer is { type: 'slot' } & FigmaTemplateSlot => layer.type === 'slot');
export const figmaTemplateTexts = (template: StationCFigmaTemplate): FigmaTemplateText[] =>
  template.layers.filter((layer): layer is { type: 'text' } & FigmaTemplateText => layer.type === 'text' && !layer.fixed);
/** AI 초안으로 채울 수 있는 템플릿 — 편집 문구가 있고 본문이 실시간 위젯이 아닌 것(크리스마스·오늘의 정책 소식 제외). */
export const isAiDraftTemplate = (template: StationCFigmaTemplate): boolean =>
  figmaTemplateTexts(template).length > 0 && !template.layers.some((layer) => layer.type === 'widget');

/** Fits the 2160×3840 design into the canvas. Bundled graphics keep `asset:` ids — see `resolveStationCTemplateAssets`. */
export function buildStationCFigmaTemplate(options: StationCFigmaOptions): StationCFigmaDraft {
  const { templateId, canvasWidth, canvasHeight, durationSec, textValues = {}, slotMedia = {}, mediaList = [] } = options;
  const template = STATIONC_FIGMA_TEMPLATES.find((item) => item.id === templateId);
  if (!template) throw new Error('템플릿을 선택해 주세요.');
  if (![canvasWidth, canvasHeight].every((value) => Number.isSafeInteger(value) && value > 0)) throw new Error('대지 크기를 확인해 주세요.');
  if (!Number.isFinite(durationSec) || durationSec < 1) throw new Error('재생시간은 1초 이상이어야 합니다.');
  const scale = Math.min(canvasWidth / FIGMA_DESIGN.width, canvasHeight / FIGMA_DESIGN.height);
  const left = (canvasWidth - FIGMA_DESIGN.width * scale) / 2, top = (canvasHeight - FIGMA_DESIGN.height * scale) / 2;
  const scaleRadius = (radius: NoticeCornerRadius): NoticeCornerRadius =>
    typeof radius === 'number' ? radius * scale : [radius[0] * scale, radius[1] * scale, radius[2] * scale, radius[3] * scale];
  const mediaFor = (slotDef: FigmaTemplateSlot): { mediaId: string; video: boolean } | null => {
    const picked = slotMedia[slotDef.id];
    if (picked) {
      const item = mediaList.find((media) => media.id === picked);
      const videoOk = slotDef.kind === 'background';
      // Only media the current editor can see may be referenced.
      if (!item || (item.mediaType !== 'image' && !(videoOk && item.mediaType === 'video'))) {
        throw new Error(videoOk ? `'${slotDef.label}'에 현재 목록의 이미지 또는 영상을 선택해 주세요.` : `'${slotDef.label}'에 현재 목록의 이미지를 선택해 주세요.`);
      }
      return { mediaId: item.id, video: item.mediaType === 'video' };
    }
    return slotDef.defaultAsset ? { mediaId: assetMediaId(slotDef.defaultAsset), video: isVideoAsset(STATIONC_TEMPLATE_ASSETS[slotDef.defaultAsset]) } : null;
  };
  const layers: StationCFigmaLayer[] = [];
  template.layers.forEach((node, index) => {
    const [x, y, width, height] = node.box;
    const base = {
      id: `stationc-${crypto.randomUUID()}`, name: node.label, z: index, opacity: 1, rotation: 0,
      x: left + x * scale, y: top + y * scale, width: Math.max(1, width * scale), height: Math.max(1, height * scale),
      // Entrances all land before 1s (the editor's minimum scene length); null = until the scene ends.
      timeline: { startSec: node.stage * 0.15, endSec: null },
    };
    if (node.type === 'text') {
      layers.push({ ...base, kind: 'text', opacity: node.opacity ?? 1, text: node.fixed ? node.text : textValues[node.id] ?? node.text,
        fontSize: Math.max(8, Math.min(1000, node.fontSize * scale)), fontWeight: node.fontWeight, color: node.color,
        backgroundColor: node.backgroundColor ?? null, align: node.align ?? 'left', verticalAlign: node.verticalAlign ?? 'middle',
        paddingX: 0, paddingY: 0, lineHeight: node.lineHeight,
        ...(node.letterSpacing ? { letterSpacing: node.letterSpacing * scale } : {}),
        ...(node.fontFamily ? { fontFamily: node.fontFamily } : {}),
        ...(node.cornerRadius !== undefined ? { cornerRadius: scaleRadius(node.cornerRadius) } : {}) });
      return;
    }
    if (node.type === 'widget') {
      layers.push({ ...base, kind: 'dataWidget', source: node.source, title: node.title,
        fontSize: Math.max(8, Math.min(1000, node.fontSize * scale)), color: node.color, backgroundColor: null,
        ...(node.fontWeight ? { fontWeight: node.fontWeight } : {}), ...(node.fontFamily ? { fontFamily: node.fontFamily } : {}),
        ...(node.align ? { align: node.align } : {}), ...(node.weatherLayout ? { weatherLayout: node.weatherLayout } : {}),
        ...(node.weatherFields ? { weatherFields: node.weatherFields } : {}) });
      return;
    }
    const media = node.type === 'art' ? { mediaId: assetMediaId(node.asset), video: false } : mediaFor(node);
    if (!media) return;
    const crop = node.type === 'slot' && node.defaultCrop && !slotMedia[node.id] ? node.defaultCrop : { mediaScale: 1, mediaOffsetX: 0, mediaOffsetY: 0 };
    const image = { ...base, mediaId: media.mediaId, fit: node.fit, ...crop,
      ...(node.type === 'slot' && node.cornerRadius !== undefined ? { cornerRadius: scaleRadius(node.cornerRadius) } : {}) };
    layers.push(media.video ? { ...image, kind: 'video', loop: true, muted: true, repeat: 'loop' } : { ...image, kind: 'image' });
  });
  return { canvasWidth, canvasHeight, canvasFill: template.canvasFill, canvasFillOpacity: 100, durationSec, layers };
}

/** Bundled assets referenced by a draft (deduplicated, in draw order). */
export function draftAssets(draft: { layers: readonly NoticeLayer[] }): StationCTemplateAsset[] {
  const seen = new Map<string, StationCTemplateAsset>();
  for (const layer of draft.layers) {
    if (!('mediaId' in layer)) continue;
    const asset = assetForMediaId(layer.mediaId);
    if (asset && !seen.has(asset.key)) seen.set(asset.key, asset);
  }
  return [...seen.values()];
}

/** Library item already holding a bundled asset — matched by the distinctive originalName so an
 *  organisation uploads each file once no matter how many slides use the template. */
export const findAssetMedia = (asset: StationCTemplateAsset, mediaList: readonly MediaItem[]): MediaItem | undefined =>
  mediaList.find((item) => item.mediaType === (isVideoAsset(asset) ? 'video' : 'image') && item.originalName === asset.fileName);

/** Replaces `asset:` placeholders with real media ids, uploading files the library does not have yet. */
/** 템플릿 그래픽("asset:")을 라이브러리 미디어로 바꿔 끼운다 — 이미 있으면 그대로 쓰고, 없으면 올린다. 슬라이드 저장 직전에 부른다. */
export async function resolveStationCTemplateAssets<T extends { layers: readonly NoticeLayer[] }>(
  draft: T, mediaList: readonly MediaItem[],
  upload: (file: File) => Promise<MediaItem>,
  fetchAsset: (asset: StationCTemplateAsset) => Promise<Blob> = async (asset) => {
    const response = await fetch(asset.url);
    if (!response.ok) throw new Error(`템플릿 자산을 불러오지 못했습니다: ${asset.label}`);
    return response.blob();
  },
): Promise<T> {
  const ids = new Map<string, string>();
  for (const asset of draftAssets(draft)) {
    const existing = findAssetMedia(asset, mediaList);
    if (existing) { ids.set(asset.key, existing.id); continue; }
    const blob = await fetchAsset(asset);
    const uploaded = await upload(new File([blob], asset.fileName, { type: asset.mime }));
    ids.set(asset.key, uploaded.id);
  }
  return { ...draft, layers: draft.layers.map((layer) => {
    if (!('mediaId' in layer)) return layer;
    const asset = assetForMediaId(layer.mediaId);
    return asset ? { ...layer, mediaId: ids.get(asset.key) ?? layer.mediaId } : layer;
  }) as T['layers'] };
}

export { STATIONC_TEMPLATE_ASSETS, assetForMediaId };
